import { useEffect } from 'react';
import { useCurrentPerson } from '../hooks/useCurrentPerson';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { createLogger, LogLevel } from '@/lib/logger';
import { useOrganizationClients } from '@/components/dashboard/useOrganizationClients';
import { useClientTableData } from '@/components/dashboard/useClientTableData';
import { useRecentDocumentsData } from '@/components/dashboard/useRecentDocumentsData';
import { useSyncStatus } from '@/components/dashboard/useSyncStatus';
import { ClientTableContent } from '@/components/dashboard/ClientTable';
import { RecentDocumentsListContent } from '@/components/dashboard/RecentDocumentsList';
import RecentlyViewedClients from '@/components/dashboard/RecentlyViewedClients';

const logger = createLogger('Home', LogLevel.WARN);

export default function Home() {
  const { currentPerson, organizationId } = useCurrentPerson();
  const base = useOrganizationClients(organizationId);
  const { searchQuery, setSearchQuery, filteredClients, unsyncedClientIds } = useClientTableData(base);
  const { recentDocuments, clientsMap, templatesMap } = useRecentDocumentsData(organizationId, base);
  const { recentClients } = useSyncStatus(base);

  useEffect(() => {
    if (organizationId && navigator.onLine) {
      dispatchDataEvent(DataEvents.REFRESH).catch(err => {
        logger.error('Failed to refresh data on dashboard load:', err);
      });
    }
  }, [organizationId]);

  const firstName = currentPerson?.first_name || currentPerson?.official_full_name?.split(' ')[0] || '';

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      <div className="px-6 pt-6 pb-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          Welcome, {firstName || 'there'}!
        </h1>
      </div>

      <div className="px-6 py-4 flex-1 flex gap-6 min-h-0">
        <ClientTableContent
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredClients={filteredClients}
          unsyncedClientIds={unsyncedClientIds}
        />

        <div className="w-80 flex-shrink-0 flex flex-col gap-6 min-h-0">
          <RecentDocumentsListContent
            documents={recentDocuments}
            clientsMap={clientsMap}
            templatesMap={templatesMap}
          />
          <RecentlyViewedClients clients={recentClients} />
        </div>
      </div>
    </div>
  );
}
